import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import Button from "../../components/Button";
import DataTable, { type Column } from "../../components/DataTable";
import Badge from "../../components/Badge";
import TextInput from "../../components/TextInput";
import { useMembersList } from "../../../hooks/useMembersList";
import { useParcoursList } from "../../../hooks/useParcoursList";
import { useAssignParcours } from "../../../hooks/useAssignParcours";
import { useUnassignParcours } from "../../../hooks/useUnassignParcours";
import type { ParcoursResponse } from "../../../api/parcoursApi";

export default function MemberAssignmentsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const members = useMembersList();
  const parcoursPage = useParcoursList({ page: 0, size: 100, sort: "title,asc" });
  const assign = useAssignParcours();
  const unassign = useUnassignParcours();
  const [filter, setFilter] = useState("");

  const member = members.data?.find((m) => m.id === id);
  if (members.isLoading) return <div className="bo-loading">Chargement…</div>;
  if (!member) return <div className="bo-error-text" style={{ textAlign: "center" }}>Membre introuvable.</div>;

  const assigned = new Set(member.assignedParcoursIds);
  const rows = (parcoursPage.data?.content ?? []).filter((p) =>
    !filter ? true : p.title.toLowerCase().includes(filter.toLowerCase()),
  );

  const columns: Column<ParcoursResponse>[] = [
    { key: "title", header: "Parcours", render: (row) => <span style={{ fontWeight: 600 }}>{row.title}</span> },
    { key: "commune", header: "Commune", render: (row) => row.mainCommune ?? "—" },
    {
      key: "assigned",
      header: "État",
      render: (row) =>
        assigned.has(row.id) ? <Badge tone="success">Assigné</Badge> : <Badge tone="neutral">Non assigné</Badge>,
    },
    {
      key: "actions",
      header: "",
      render: (row) =>
        assigned.has(row.id) ? (
          <Button variant="ghost" onClick={() => unassign.mutate({ memberId: member.id, parcoursId: row.id })}>
            Retirer
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => assign.mutate({ memberId: member.id, parcoursId: row.id })}
            disabled={member.role !== "EDITOR"}
          >
            Assigner
          </Button>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={`Assignations · ${member.userName ?? member.userId}`}
        description={
          member.role === "EDITOR"
            ? "Sélectionnez les parcours que cet éditeur peut modifier."
            : "Les administrateurs ont déjà accès à tous les parcours."
        }
        actions={
          <Button variant="ghost" onClick={() => navigate("/admin/members")}>
            Retour
          </Button>
        }
      />
      <div className="bo-toolbar">
        <TextInput
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtrer les parcours…"
          className="bo-search"
        />
      </div>
      <DataTable rows={rows} columns={columns} rowKey={(r) => r.id} loading={parcoursPage.isLoading} />
    </div>
  );
}
